import { ContentViewData, PunchListItem } from './../types.ts/story';
import { AIPrompt } from '../types.ts/ai_prompts';
import axios from 'axios';
import TurndownService from 'turndown';
import { TalkingPointContent } from '../types.ts/general';
import VideoCreatorStore from '@src/stores/VideoCreatorStore';
import { logChatGptOutput } from '@src/utility/datadog';
import { AIPromptRepository } from '@src/repositories';
import { TranscriptionStore } from '@src/stores-v2';
import { Speaker } from '@src/components/content-components/SpeakerSelector';
const turndownService = new TurndownService();

type SupportedKey =
  | 'Email'
  | 'Quotes'
  | 'Blog'
  | 'Social Post'
  | 'Music Selection'
  | 'Photo Punchlist';

type GenerateStreamResponseParams = {
  key: SupportedKey;
  template?: AIPrompt;
  version?: '1' | '2';
  type?: 'existing' | 'new' | 'regenerate';
  customInstructions?: string;
  revision?: string;
  brandVoice?: string;
  avoidWords?: string;
  // For speaker filtering
  selectedSpeakers?: Speaker[];
};

export default class ChatGPTService {
  protected videoCreator: VideoCreatorStore;
  protected transcriptionStore: TranscriptionStore;
  protected aiPromptRepository: AIPromptRepository;
  public streamProcessDone = false;
  private controller: AbortController | null = null;
  public talkingPointController: AbortController | null = null;

  constructor(
    videoCreator: VideoCreatorStore,
    aiPromptRepository: AIPromptRepository,
  ) {
    this.videoCreator = videoCreator;
    this.transcriptionStore = videoCreator.rootStore.transcriptionStore;
    this.aiPromptRepository = aiPromptRepository;
  }

  fetchAiPrompt = async (key: string) => {
    const prompt = await this.aiPromptRepository.getPromptByTitle(key);
    return prompt;
  };

  private getExistingResponseData = (key: SupportedKey) => {
    return (
      this.videoCreator.contentStudioGeneratedContent?.[key]?.content
        ?.response || ''
    );
  };

  public getTranscript = (
    key: SupportedKey,
    selectedSpeakers?: Speaker[],
  ): string | undefined => {
    try {
      let elements;

      // Get the right elements based on content type
      if (key === 'Music Selection' || key === 'Photo Punchlist') {
        elements = this.transcriptionStore
          .getFinalTranscriptionElements()
          ?.filter((el: any) => el.state !== 'removed' && el.state !== 'cut');
      } else {
        elements = this.transcriptionStore.originalTranscription?.elements;
      }

      // Safety check - if no elements, return early
      if (!elements || elements.length === 0) {
        console.warn('No transcript elements found for key:', key);
        return '';
      }
      // Apply speaker filtering if specified
      if (selectedSpeakers && selectedSpeakers.length > 0) {
        // Filter elements to include only those from selected speakers
        const filteredElements = elements.filter(
          (el: any) =>
            el.speaker_label &&
            selectedSpeakers?.some((s) => s.speakerLabel === el.speaker_label),
        );

        // If filtering removed all elements, warn but continue with all elements
        if (filteredElements.length === 0) {
          console.warn(
            'Speaker filtering removed all elements. Using all transcript elements.',
          );
        } else {
          elements = filteredElements;
        }
      }

      // Format transcript with speaker information if filtering is applied
      if (
        selectedSpeakers &&
        selectedSpeakers.length > 0 &&
        elements.length > 0
      ) {
        // Group contiguous elements by speaker
        // with a special prefix for each speaker
        // and a new line between each speaker
        let formattedText = '';
        let currentSpeaker = '';
        let currentText = '';

        // Process each element
        elements.forEach((element: any, index: number) => {
          if (!element) return; // Skip null/undefined elements

          const speakerLabel = element.speaker_label || 'unknown';
          const elementText = element.value || '';

          // Skip empty text elements
          if (!elementText.trim()) return;

          // If speaker changed, add the previous speaker's text and start new speaker
          if (speakerLabel !== currentSpeaker) {
            // Add the previous speaker's text if there was one
            if (currentSpeaker && currentText) {
              const speakerName =
                selectedSpeakers?.find((s) => s.speakerLabel === currentSpeaker)
                  ?.name || currentSpeaker;
              formattedText += `${speakerName}: ${currentText}\n\n`;
            }

            // Start tracking the new speaker
            currentSpeaker = speakerLabel;
            currentText = elementText;
          } else {
            // Continue with the same speaker, append text
            currentText += ` ${elementText}`;
          }

          // Handle the last element
          if (index === elements.length - 1 && currentText) {
            const speakerName =
              selectedSpeakers?.find((s) => s.speakerLabel === currentSpeaker)
                ?.name || currentSpeaker;
            formattedText += `${speakerName}: ${currentText}`;
          }
        });

        console.log('Using formatted transcript with speakers', formattedText);

        // Fail-safe check for empty formatted text
        if (formattedText && formattedText.trim().length > 0) {
          return formattedText.trim();
        } else {
          console.warn(
            'Formatted text is empty, falling back to default formatting',
          );
        }
      }

      // Default formatting - just join all text
      console.log('Using default transcript formatting');
      const defaultText = elements
        .map((e: any) => e?.value || '')
        .join('')
        .trim();

      // Final safety check
      if (!defaultText) {
        console.warn('Default transcript formatting produced empty text');
        return 'No transcript text available';
      }

      return defaultText;
    } catch (error) {
      console.error('Error in getTranscript method:', error);
      return 'Error processing transcript';
    }
  };

  private async getResponseInputData(params: GenerateStreamResponseParams) {
    const {
      key,
      template,
      version = '1',
      type = 'existing',
      customInstructions,
      revision,
      brandVoice,
      avoidWords,
      selectedSpeakers,
    } = params;
    const transcript = this.getTranscript(key, selectedSpeakers);
    let aiPrompt = template;
    if (!aiPrompt && customInstructions) {
      aiPrompt = await this.fetchAiPrompt(`Custom ${key}`);
    }
    if (!aiPrompt) {
      aiPrompt = await this.fetchAiPrompt(key);
    }

    if (!aiPrompt || !transcript || !this.videoCreator.story?.id) return;
    let existingResponse: any =
      type === 'new' ? '' : this.getExistingResponseData(key);
    if (version === '2') {
      existingResponse = { ...aiPrompt, response: existingResponse.response };
    }

    let systemMessage = aiPrompt.description;
    if (customInstructions) systemMessage += `\n\n${customInstructions}`;

    let followUp = '';
    if (type === 'regenerate') followUp += aiPrompt.followUp;
    if (revision) followUp += `${followUp ? '\n\n' : ''}${revision}`;

    // Always add formatting instruction for quotes
    if (key === 'Quotes') {
    }

    // Add speaker context to the prompt if filtering is applied
    if (selectedSpeakers && selectedSpeakers.length > 0) {
      // Find speaker labels from transcript
      const allElements =
        this.transcriptionStore.originalTranscription?.elements || [];
      const allSpeakerLabels = Array.from(
        new Set(
          allElements
            .filter((el: any) => el.speaker_label)
            .map((el: any) => el.speaker_label),
        ),
      );

      // Only add filtering instructions if not selecting all speakers
      if (selectedSpeakers.length < allSpeakerLabels.length) {
        // Format speaker names for display in prompt
        const speakerNames = selectedSpeakers
          .map((s) => s.name || `Speaker ${s.speakerLabel.replace('spk_', '')}`)
          .join(', ');

        // Base speaker context
        let speakerContext = '';

        const speakerTitlePrompt = await this.fetchAiPrompt('Speaker Title');
        if (speakerTitlePrompt) {
          systemMessage += `${systemMessage ? '\n\n' : ''}${
            speakerTitlePrompt.description
          }\nSpeaker titles: \n`;
          selectedSpeakers.forEach((speaker) => {
            if (speaker.title) {
              systemMessage += `${speaker.name}: ${speaker.title}\n`;
            }
          });
        }

        const speakerBioPrompt = await this.fetchAiPrompt('Speaker Bio');
        if (speakerBioPrompt) {
          systemMessage += `${systemMessage ? '\n\n' : ''}${
            speakerBioPrompt.description
          }\nSpeaker bios: \n`;
          selectedSpeakers.forEach((speaker) => {
            if (speaker.bio) {
              systemMessage += `${speaker.name}: ${speaker.bio}\n`;
            }
          });
        }

        // Customize speaker instructions based on content type
        switch (key) {
          case 'Quotes':
            const formattingInstruction = `IMPORTANT: End each quote with the speaker's name in the format " --- Speaker Name". For example: "This is an important quote. --- John Smith". Every quote MUST be formatted this way with exactly 3 dashes to ensure proper attribution.`;
            systemMessage += `${
              systemMessage ? '\n\n' : ''
            }${formattingInstruction}`;
            speakerContext = `Please generate quotes ONLY using the words spoken by ${speakerNames}. Ignore content from other speakers.`;
            break;

          case 'Blog':
            speakerContext = `Focus the blog content primarily on perspectives and insights from ${speakerNames}. While you may reference other speakers for context, make ${speakerNames} the central voice(s) of the blog. When including direct quotes, attribute them properly to the speaker.`;
            break;

          case 'Social Post':
            speakerContext = `Create social posts that highlight key points made by ${speakerNames}. If directly quoting, include attribution to the speaker. Focus on the most engaging and shareable insights from these specific speakers.`;
            break;

          case 'Email':
            speakerContext = `Frame the email content around insights from ${speakerNames}, using their perspectives as the main value proposition while maintaining a professional email tone. Include relevant quotes with proper attribution when appropriate.`;
            break;

          // Add default case for other content types
          default:
            speakerContext = `Focus on content from ${speakerNames} when generating your response. Prioritize their perspectives and insights over other speakers in the transcript.`;
        }

        // Add the speaker context to the prompt
        systemMessage += `\n\n${speakerContext}`;

        // Add voice descriptions if available for selected speakers
        const speakersWithVoice = selectedSpeakers.filter(
          (s) => s.voice && s.voice.trim().length > 0,
        );

        if (speakersWithVoice.length > 0) {
          let voiceContext =
            '\n\nWhen generating content, match these speaking styles:';

          speakersWithVoice.forEach((speaker) => {
            const speakerName =
              speaker.name ||
              `Speaker ${speaker.speakerLabel.replace('spk_', '')}`;
            voiceContext += `\n- ${speakerName}: ${speaker.voice}`;
          });

          // Add specific instructions based on content type
          switch (key) {
            case 'Quotes':
              voiceContext += `\n\nUse these voice descriptions to select quotes that best represent each speaker's unique voice and style.`;
              break;

            case 'Blog':
              voiceContext += `\n\nWhen crafting the blog, ensure the overall tone reflects these voice characteristics, especially when paraphrasing their perspectives.`;
              break;

            case 'Social Post':
              voiceContext += `\n\nMatch these voice characteristics when creating social posts to maintain authenticity.`;
              break;

            case 'Email':
              voiceContext += `\n\nWhen writing the email, incorporate these voice characteristics into the messaging while maintaining professionalism.`;
              break;
          }

          // Add the voice context to the prompt
          systemMessage += voiceContext;
        }
      }
    }

    if (brandVoice) {
      const brandVoicePrompt = await this.fetchAiPrompt('Brand Voice');
      if (brandVoicePrompt) {
        systemMessage += `${systemMessage ? '\n\n' : ''}${
          brandVoicePrompt.description
        }\nBrand voice: ${brandVoice}`;
      }
    }

    if (avoidWords) {
      const avoidWordsPrompt = await this.fetchAiPrompt('Avoid Words');
      if (avoidWordsPrompt) {
        systemMessage += `${systemMessage ? '\n\n' : ''}${
          avoidWordsPrompt.description
        }\nAvoid words: ${avoidWords}`;
      }
    }

    return {
      systemMessage,
      transcript,
      existingResponse,
      followUp,
    };
  }

  private updateGeneratedContent = (
    key: SupportedKey,
    response: string = '',
  ) => {
    let generatedContent = this.videoCreator.contentStudioGeneratedContent;
    this.videoCreator.contentStudioGeneratedContent = {
      ...(generatedContent || {}),
      [key]: {
        ...(generatedContent?.[key] || {}),
        content: {
          ...(generatedContent?.[key]?.content || {}),
          response,
        },
      },
    } as ContentViewData;
  };

  public async regenerateStreamResponse(
    params: GenerateStreamResponseParams & { setLoading: (e: boolean) => void },
  ) {
    const {
      key,
      template,
      setLoading,
      version = '1',
      type = 'existing',
      customInstructions,
      revision,
      brandVoice,
      avoidWords,
      selectedSpeakers,
    } = params;
    try {
      // console.log('Starting regenerateStreamResponse with params:', {
      //   key,
      //   template: template?.title,
      //   hasSelectedSpeakers: !!selectedSpeakers,
      //   selectedSpeakerCount: selectedSpeakers?.length || 0,
      //   selectedSpeakers: selectedSpeakers?.map((s) => s.speakerLabel),
      //   type,
      // });

      // Cancel any in-progress requests
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }

      // Create a new abort controller
      this.controller = new AbortController();
      const signal = this.controller.signal;

      // Prepare the request data
      const items = await this.getResponseInputData({
        key,
        template,
        version,
        type,
        customInstructions,
        revision,
        brandVoice,
        avoidWords,
        selectedSpeakers,
      });

      // Check if we have all the required data
      if (!items) {
        console.error(
          'getResponseInputData returned null or undefined. API request aborted.',
        );
        setLoading(false);
        return;
      }

      const { systemMessage, transcript, existingResponse, followUp } = items;
      const storyId = this.videoCreator.story?.id;

      // Validate required data
      if (!storyId) {
        console.error('No story ID available. API request aborted.');
        setLoading(false);
        return;
      }

      if (!transcript || transcript.trim().length === 0) {
        console.error('No transcript text available. API request aborted.');
        setLoading(false);
        return;
      }

      // console.log('Got response input data:', {
      //   hasSystemMessage: !!systemMessage,
      //   hasTranscript: !!transcript,
      //   transcriptLength: transcript?.length || 0,
      //   hasExistingResponse: !!existingResponse,
      //   hasFollowUp: !!followUp,
      //   storyId,
      // });

      // Construct API URL
      const apiUrl = `${
        process.env.REACT_APP_API_URL
      }/api/aiprompts/regenerate-gpt-response-stream${
        version === '2' ? '-v2' : ''
      }`;
      // console.log('Calling API at:', apiUrl);

      // Prepare request body
      const requestBody = {
        systemMessage,
        transcript,
        existingResponse,
        followUp,
        storyId,
      };

      try {
        // Clear existing content before making the request
        this.updateGeneratedContent(key, '');

        // Make API request
        const res = await fetch(apiUrl, {
          signal,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });

        // Handle error responses
        if (!res.ok) {
          console.error(
            'API response not OK. Status:',
            res.status,
            res.statusText,
          );
          try {
            const errorText = await res.text();
            console.error('Error response:', errorText);

            // Update content with error message for user
            this.updateGeneratedContent(
              key,
              `Error generating content: ${res.status} ${res.statusText}`,
            );
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
          }
          setLoading(false);
          return;
        }

        // Process the response stream
        const data = res.body;
        if (!data) {
          console.error('Response body is null or undefined');
          this.updateGeneratedContent(key, 'Error: No response data received');
          setLoading(false);
          return;
        }

        // Set up stream processing
        const reader = data.getReader();
        const decoder = new TextDecoder();

        // Mark content as generated
        if (this.videoCreator.contentStudioGeneratedContent?.[key]) {
          this.videoCreator.contentStudioGeneratedContent[
            key
          ].hasBeenGenerated = true;
          this.videoCreator.contentStudioGeneratedContent[key].usedSpeakers =
            !!selectedSpeakers?.length;
        }

        // Process the stream
        await this.processGeneratedContentByChunk(
          reader,
          decoder,
          setLoading,
          key,
          key, // Use the key as the logging segment
        );
      } catch (fetchError: any) {
        // Handle fetch errors
        console.error('Fetch error:', fetchError.message);
        this.updateGeneratedContent(key, `Error: ${fetchError.message}`);
        setLoading(false);
      }
    } catch (err: any) {
      // Handle top-level errors
      if (err.name === 'AbortError') {
        console.log('Request aborted', err.message);
      } else {
        console.error('Error occurred:', err);
        // Display error to user
        this.updateGeneratedContent(key, `An error occurred: ${err.message}`);
      }
      setLoading(false);
    }
  }

  public async matchPunchListWithSources(
    punchList: PunchListItem[],
    sources: string[],
  ): Promise<{ punchList: PunchListItem[] }> {
    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/aiprompts/match-punchlist-with-sources`,
      { punchList, sources, storyId: this.videoCreator.story?.id },
    );
    return data;
  }

  public async generatePunchListItem(line: string) {
    const storyId = this.videoCreator.story?.id;
    if (!line || !storyId) return;

    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/aiprompts/generate-punchlist-item`,
      { line, storyId },
    );
    return data;
  }

  private async processGeneratedContentByChunk(
    reader: any,
    decoder: TextDecoder,
    setLoading: (e: boolean) => void,
    key: SupportedKey,
    loggingKey: string,
  ) {
    let i = 0;
    while (!this.streamProcessDone) {
      const { value, done: doneReading } = await reader.read();
      this.streamProcessDone = doneReading;
      let chunk = decoder.decode(value);
      let addition = '';

      if (key === 'Email') {
        if (i === 0 && !chunk.includes('##')) addition = '## ';
        if (chunk === ' ') chunk = '\n\n';
        if (chunk.includes('Subject:')) {
          chunk = chunk.replace('Subject:', '').trim();
        }
      } else if (key === 'Blog') {
        this.videoCreator.selectedGeneratedContent['blog'] = {
          type: doneReading ? 'generated' : 'generating',
        };
      } else if (key === 'Social Post') {
        this.videoCreator.selectedGeneratedContent['socialPost'] = {
          type: doneReading ? 'generated' : 'generating',
        };
      } else if (key === 'Quotes') {
        // Ensure each quote has the proper formatting if it doesn't already
        if (doneReading) {
          // Access the full text after processing is complete
          const existingResponse = this.getExistingResponseData(key);
          // console.log('Existing response:', existingResponse);
        }
      }

      const existingResponse = this.getExistingResponseData(key);
      const text = existingResponse + addition + chunk;
      this.updateGeneratedContent(key, text);
      if (doneReading) {
        logChatGptOutput({ [loggingKey]: text });
        setLoading(false);
      }
      i++;
    }
    if (this.streamProcessDone) {
      this.controller = null;
      this.streamProcessDone = false;
    }
  }

  async generateTalkingPoint({
    setLoading,
    brandText,
    key = null,
    template,
    avoidWords,
  }: {
    setLoading: (e: boolean) => void;
    brandText?: string;
    key?: (keyof TalkingPointContent & 'all') | null;
    template?: AIPrompt | null;
    avoidWords?: string;
  }) {
    try {
      if (this.talkingPointController) {
        this.talkingPointController.abort();
      }
      this.talkingPointController = new AbortController();
      const signal = this.talkingPointController.signal;

      const transcript = this.transcriptionStore
        .getFinalTranscriptionElements()
        ?.map((e) => e.value || '')
        .join('');

      let prompts = template;
      if (!prompts) {
        prompts = await this.fetchAiPrompt('Fundraising prompt');
      }
      if (!prompts?.promptFields || !transcript) return;

      const model = 'gpt-4o';
      const temperature = 0;

      if (!key || key === 'all') {
        if (!key || !this.videoCreator.talkingPointContent) {
          this.videoCreator.talkingPointContent = {} as TalkingPointContent;
        } else {
          this.videoCreator.talkingPointContent = Object.fromEntries(
            Object.entries(this.videoCreator.talkingPointContent).map(
              ([key, value]) => [key, { prompt: value.prompt, content: '' }],
            ),
          ) as TalkingPointContent;
        }

        for (let prompt of prompts.promptFields) {
          await this.runTalkingPointAi(
            transcript,
            prompts.description,
            setLoading,
            prompt,
            temperature,
            model,
            key,
            brandText,
            avoidWords,
            signal,
          );
        }
      } else {
        const prompt = prompts.promptFields.find((f) => f.name === key);
        if (!prompt) return;

        await this.runTalkingPointAi(
          transcript,
          prompts.description,
          setLoading,
          prompt,
          temperature,
          model,
          key,
          brandText,
          avoidWords,
          signal,
        );
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Request aborted', error, error.message);
      } else {
        console.error('Error occurred:', error);
      }
    }
  }

  getPrevContent(content: TalkingPointContent | null, promptName: string) {
    try {
      if (content && promptName in content) {
        return content[promptName as keyof TalkingPointContent];
      }
      return { prompt: '', content: '' };
    } catch (error) {
      console.log('Error: ', error);
    }
  }

  async runTalkingPointAi(
    transcript: string,
    description: string,
    setLoading: (e: boolean) => void,
    prompt: AIPrompt['promptFields'][0],
    temperature: number,
    model: string,
    key: (keyof TalkingPointContent & 'all') | null,
    brandText: string | undefined,
    avoidWords: string | undefined,
    signal: AbortSignal,
  ) {
    let prevContent = '';
    let prevPrompt = '';
    if (key) {
      const prevData = this.getPrevContent(
        this.videoCreator.talkingPointContent,
        prompt.name,
      );
      prevContent = turndownService.turndown(prevData?.content ?? '');
      prevPrompt = prevData?.prompt ?? '';
    }

    const promptDescription = `${prompt.description}.  Return only markdown with no other comments.`;
    let currentPrompt = `${promptDescription}`;

    if (brandText) currentPrompt += ` ${brandText}`;

    if (avoidWords) {
      const avoidWordsPrompt = await this.fetchAiPrompt('Avoid Words');
      if (avoidWordsPrompt) {
        currentPrompt += `${currentPrompt ? '\n\n' : ''}${
          avoidWordsPrompt.description
        }: ${avoidWords}`;
      }
    }

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/aiprompts/stream-talking-point-response`,
      {
        signal,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transcript,
          systemMessage: description,
          prevPrompt: prevPrompt || promptDescription,
          currentPrompt,
          temperature,
          model,
          prevContent,
        }),
      },
    );

    const data = res.body;
    if (!data) return;
    const reader = data.getReader();
    const decoder = new TextDecoder();
    let done = false;

    if (key && this.videoCreator.talkingPointContent?.hasOwnProperty(key)) {
      this.videoCreator.talkingPointContent[
        key as keyof TalkingPointContent
      ].content = '';
    }

    while (!done) {
      const { value, done: doneReading } = await reader.read();
      done = doneReading;
      let chunk = decoder.decode(value);
      setLoading(false);

      const key = prompt.name as keyof TalkingPointContent;
      const existingContent = (this.videoCreator.talkingPointContent ||
        {}) as TalkingPointContent;

      const content = (existingContent[key]?.content || '') + chunk;
      this.videoCreator.talkingPointContent = {
        ...(existingContent as TalkingPointContent),
        [key]: {
          content,
          prompt: currentPrompt,
        },
      };
      if (done) {
        logChatGptOutput({ 'stream-talking-point-response': content });
      }
    }
  }

  regenerateArtifactTitle = async ({
    artifactId,
    storyId,
    organizationId,
  }: {
    artifactId: string;
    storyId: string | undefined;
    organizationId: string | undefined;
  }) => {
    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/aiprompts/regenerate-artifact-title`,
      {
        artifactId,
        storyId,
        organizationId,
      },
    );
    return data;
  };
}
